import {
    FeedbackRequestTodoWithDetail,
    Todo,
    TodoWithGoal,
} from '@/common/types/databaseTypes';
import { useWorkspaceMemberGoals } from '@/features/goal/services/useGoalQueries';
import { TodoCompleteViewOption } from '@/features/team_member/types/todoViewTypes';
import TodoCheckBox from '@/features/todo/components/TodoCheckBox';
import {
    useGetDoneTodos,
    useGetNotDoneTodos,
    useUpdateTodo,
} from '@/features/todo/services/useTodoQueries';
import { useAuthStore } from '@/stores/authStore';
import {
    Box,
    Center,
    Group,
    Loader,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { IconCircleMinus, IconCirclePlus } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import useFeedbackModalStore from '../hooks/useFeedbackModalStore';

// 공통 로직들
interface TodoGroupRendererProps<T> {
    todosByGoalId: Record<string, { goalTitle: string; todos: T[] }>;
    todosWithoutGoal: T[];
    renderTodoItem: (todo: T) => JSX.Element;
    handleSelectAll: (goalId: string) => void;
    isGoalTodosAllSelected: (goalId: string) => boolean;
}

function TodoGroupRenderer<T>({
    todosByGoalId,
    todosWithoutGoal,
    renderTodoItem,
    handleSelectAll,
    isGoalTodosAllSelected,
}: TodoGroupRendererProps<T>) {
    const { t } = useTranslation('feedback');

    return (
        <Stack gap="xs" mt="md">
            {/* 목표별 할 일 */}
            {Object.entries(todosByGoalId).map(
                ([goalId, { goalTitle, todos }]) => (
                    <Stack key={goalId} gap={4} mb={8}>
                        <Group justify="space-between">
                            <Text px={4} size="xs" c="dimmed">
                                {goalTitle}
                            </Text>
                            <UnstyledButton
                                onClick={() => handleSelectAll(goalId)}
                            >
                                <Text size="xs" c="dimmed">
                                    {isGoalTodosAllSelected(goalId)
                                        ? t('todoList.unselectAll')
                                        : t('todoList.selectAll')}
                                </Text>
                            </UnstyledButton>
                        </Group>
                        {todos.map(renderTodoItem)}
                    </Stack>
                )
            )}

            {/* 목표가 없는 할 일 */}
            {todosWithoutGoal.length > 0 && (
                <Stack gap={4} mb={8}>
                    <UnstyledButton onClick={() => handleSelectAll('')}>
                        <Text size="xs" c="dimmed" ta="right">
                            {isGoalTodosAllSelected('')
                                ? t('todoList.unselectAll')
                                : t('todoList.selectAll')}
                        </Text>
                    </UnstyledButton>
                    {todosWithoutGoal.map(renderTodoItem)}
                </Stack>
            )}
        </Stack>
    );
}

function TodoItem({ todo }: { todo: Partial<Todo> }) {
    const updateTodo = useUpdateTodo();
    const { user } = useAuthStore.getState();
    // const { openDrawer } = useTodoDrawerStore();

    const handleTodoComplete = (checked: boolean) => {
        const updatedTodo = {
            id: todo.id,
            complete_date: checked ? new Date().toISOString() : null,
            update_id: user?.id ?? '',
        };

        updateTodo.mutate({ ...updatedTodo });
    };

    // const isPrivate = todo.title === PRIVATE_TODO_TITLE_KEY;

    return (
        <Group gap={8} flex={1} wrap="nowrap">
            <TodoCheckBox
                todo={todo}
                onComplete={handleTodoComplete}
                disabled={true}
            ></TodoCheckBox>
            {/* <UnstyledButton
                flex={1}
                // disabled={isPrivate}
                // onClick={() =>
                //     openDrawer({ todoId: todo.id, targetUserId: todo.user_id })
                // }
                // style={{ cursor: isPrivate ? 'default' : 'pointer' }}
            > */}
            <Text
                size="sm"
                lineClamp={1}
                // td={todo.complete_date ? 'line-through' : 'none'}
            >
                {todo.title}
            </Text>
            {/* </UnstyledButton> */}
        </Group>
    );
}

interface SelectableTodoItemProps {
    todo: Partial<Todo> | TodoWithGoal;
    todoId: string;
    isSelected: boolean;
    onToggle: ({ todoId, title }: { todoId: string; title: string }) => void;
}

function SelectableTodoItem({
    todo,
    todoId,
    isSelected,
    onToggle,
}: SelectableTodoItemProps) {
    const { t } = useTranslation(['feedback']);

    return (
        <UnstyledButton onClick={() => onToggle({ todoId, title: todo.title })}>
            <Box
                key={todoId}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '8px',
                }}
                bg={isSelected ? 'blue.0' : 'transparent'}
                px="sm"
                py="xs"
            >
                <TodoItem todo={todo} />
                <Tooltip
                    label={
                        isSelected
                            ? t('feedback:todoList.unselect')
                            : t('feedback:todoList.select')
                    }
                    withArrow
                >
                    <Box
                        c={isSelected ? 'blue' : 'gray'}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {isSelected ? (
                            <IconCircleMinus size={16} />
                        ) : (
                            <IconCirclePlus size={16} />
                        )}
                    </Box>
                </Tooltip>
            </Box>
        </UnstyledButton>
    );
}

// Todo 항목 선택 관리 훅
const useTodoSelection = () => {
    // 선택된 Todo ID 목록
    const { selectedTodoMapList, setSelectedTodoMapList } =
        useFeedbackModalStore();

    // 단일 Todo 항목 선택/해제 핸들러
    const handleTodoToggle = useCallback(
        ({ todoId, title }: { todoId: string; title: string }) => {
            setSelectedTodoMapList(
                selectedTodoMapList.map(todo => todo.id).includes(todoId)
                    ? selectedTodoMapList.filter(({ id }) => id !== todoId)
                    : [...selectedTodoMapList, { id: todoId, title }]
            );
        },
        [selectedTodoMapList, setSelectedTodoMapList]
    );

    // TodoWithGoal 또는 FeedbackRequestTodoWithDetail 구분 함수
    const isFeedbackRequestTodo = (
        todo: TodoWithGoal | FeedbackRequestTodoWithDetail
    ): todo is FeedbackRequestTodoWithDetail => {
        return (todo as FeedbackRequestTodoWithDetail).todo !== undefined;
    };

    // 목표별 전체 선택/해제 핸들러
    const handleSelectAllByGoalId = useCallback(
        (
            goalId: string,
            todos: (TodoWithGoal | FeedbackRequestTodoWithDetail)[]
        ) => {
            // 해당 목표에 속한 Todo ID 목록 수집
            const todoMapListForGoal: { id: string; title: string }[] = todos
                .filter(todo => {
                    if (goalId === '') {
                        // 목표가 없는 Todo 항목 필터링
                        return !todo.goal?.id;
                    }
                    // 특정 목표에 속한 Todo 항목 필터링
                    return todo.goal?.id === goalId;
                })
                .map(todo => {
                    if (isFeedbackRequestTodo(todo)) {
                        return { id: todo.todo.id, title: todo.todo.title };
                    }
                    return { id: todo.id, title: todo.title };
                });

            // 이미 모두 선택된 경우 선택 해제, 아니면 모두 선택
            const allSelected = todoMapListForGoal.every(({ id }) =>
                selectedTodoMapList.map(todo => todo.id).includes(id)
            );

            if (allSelected) {
                // 선택 해제
                setSelectedTodoMapList(
                    selectedTodoMapList.filter(
                        ({ id }) =>
                            !todoMapListForGoal
                                .map(todo => todo.id)
                                .includes(id)
                    )
                );
            } else {
                // 모두 선택
                setSelectedTodoMapList(
                    selectedTodoMapList.concat(
                        todoMapListForGoal
                            .filter(
                                ({ id }) =>
                                    !selectedTodoMapList
                                        .map(todo => todo.id)
                                        .includes(id)
                            )
                            .map(({ id, title }) => ({
                                id,
                                title,
                            }))
                    )
                );
            }
        },
        [selectedTodoMapList, setSelectedTodoMapList]
    );

    // 목표별로 모든 할일이 선택되었는지 확인하는 함수
    const isGoalTodosAllSelected = useCallback(
        (
            goalId: string,
            todos: (TodoWithGoal | FeedbackRequestTodoWithDetail)[]
        ) => {
            const todoIds = todos
                .filter(todo => {
                    if (goalId === '') {
                        // 목표가 없는 Todo 항목 필터링
                        return !todo.goal?.id;
                    }
                    // 특정 목표에 속한 Todo 항목 필터링
                    return todo.goal?.id === goalId;
                })
                .map(todo => {
                    if (isFeedbackRequestTodo(todo)) {
                        return todo.todo.id;
                    }
                    return todo.id;
                });

            return (
                todoIds.length > 0 &&
                todoIds.every(id =>
                    selectedTodoMapList.map(todo => todo.id).includes(id)
                )
            );
        },
        [selectedTodoMapList]
    );

    return {
        selectedTodoIds: selectedTodoMapList,
        setSelectedTodoIds: setSelectedTodoMapList,
        handleTodoToggle,
        handleSelectAllByGoalId,
        isGoalTodosAllSelected,
    };
};

interface GroupedTodos<T> {
    todosWithoutGoal: T[];
    todosByGoalId: Record<string, { goalTitle: string; todos: T[] }>;
}

// 목표별 그룹화 함수 (제네릭 버전)
function groupTodosByGoal<
    T extends { goal?: { id: string; title?: string } | null },
>(todos: T[], t: TFunction): GroupedTodos<T> {
    const todosWithoutGoal: T[] = [];
    const todosByGoalId: Record<string, { goalTitle: string; todos: T[] }> = {};

    todos.forEach(todo => {
        if (!todo.goal?.id) {
            todosWithoutGoal.push(todo);
        } else {
            if (!todosByGoalId[todo.goal.id]) {
                todosByGoalId[todo.goal.id] = {
                    goalTitle:
                        todo.goal?.title ||
                        t('feedback:todoList.goalTitlePlaceHolder'),
                    todos: [],
                };
            }
            todosByGoalId[todo.goal.id].todos.push(todo);
        }
    });

    return { todosWithoutGoal, todosByGoalId };
}

/**
 * 피드백 투두 리스트 컴포넌트
 */
interface FeedbackTodoListProps {
    userId: string;
    workspaceTeamIds: string[];
    workspaceId: string;
    selectedCompleteView: TodoCompleteViewOption;
}

export const FeedbackTodoList = ({
    userId,
    workspaceTeamIds,
    workspaceId,
    selectedCompleteView,
}: FeedbackTodoListProps) => {
    const { t } = useTranslation(['common', 'feedback']);
    const {
        selectedTodoIds,
        handleTodoToggle,
        handleSelectAllByGoalId,
        isGoalTodosAllSelected,
    } = useTodoSelection();

    // 데이터 가져오기
    const {
        data: goals = [],
        isLoading: isLoadingGoals,
        isSuccess: isGoalsSuccess,
    } = useWorkspaceMemberGoals(userId, workspaceTeamIds, workspaceId);

    // goals 배열에서 ID만 추출
    const goalIds = useMemo(() => goals.map(goal => goal.id), [goals]);

    // goals가 성공적으로 로드된 경우에만 todos 쿼리 활성화
    const { data: notDoneTodos = [], isLoading: isLoadingNotDoneTodos } =
        useGetNotDoneTodos(userId, goalIds, {
            enabled: isGoalsSuccess && goalIds.length > 0,
        });

    const { data: doneTodos = [], isLoading: isLoadingDoneTodos } =
        useGetDoneTodos(userId, goalIds, {
            enabled: isGoalsSuccess && goalIds.length > 0,
        });

    // 완료한 할 일 필터링 함수 - useCallback으로 메모이제이션
    const filterCompletedTodos = useCallback(
        (
            todos: TodoWithGoal[],
            selectedCompleteView: TodoCompleteViewOption
        ) => {
            return todos.filter(todo => {
                const completeDate = new Date(
                    todo.cancelled_date || todo.complete_date || 0
                );

                switch (selectedCompleteView) {
                    case 'week': {
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return completeDate >= weekAgo;
                    }
                    case 'two_week': {
                        const twoWeekAgo = new Date();
                        twoWeekAgo.setDate(twoWeekAgo.getDate() - 14);
                        return completeDate >= twoWeekAgo;
                    }
                    case 'month': {
                        const monthAgo = new Date();
                        monthAgo.setMonth(monthAgo.getMonth() - 1);
                        return completeDate >= monthAgo;
                    }
                    default:
                        return true;
                }
            });
        },
        []
    );

    // 완료된 할 일 기간별 필터링
    const filteredDoneTodos = useMemo(() => {
        return filterCompletedTodos(doneTodos, selectedCompleteView);
    }, [doneTodos, filterCompletedTodos, selectedCompleteView]);

    // 필터링된 완료 할 일과 미완료 할 일 합치기 (미완료는 필터 불필요)
    const filteredAllTodos = useMemo(() => {
        return [...notDoneTodos, ...filteredDoneTodos];
    }, [notDoneTodos, filteredDoneTodos]);

    // 목표별 그룹화
    const groupedTodos = useMemo(
        () => groupTodosByGoal(filteredAllTodos, t),
        [filteredAllTodos, t]
    );

    const renderTodoItem = useCallback(
        (todo: TodoWithGoal) => (
            <SelectableTodoItem
                key={todo.id}
                todo={todo}
                todoId={todo.id}
                isSelected={selectedTodoIds
                    .map(todo => todo.id)
                    .includes(todo.id)}
                onToggle={handleTodoToggle}
            />
        ),
        [selectedTodoIds, handleTodoToggle]
    );

    // 목표별 전체 선택 핸들러
    const handleSelectAll = useCallback(
        (goalId: string) => {
            handleSelectAllByGoalId(goalId, filteredAllTodos);
        },
        [handleSelectAllByGoalId, filteredAllTodos]
    );

    // 목표별로 모든 할일이 선택되었는지 확인하는 함수
    const checkIsGoalTodosAllSelected = useCallback(
        (goalId: string) => {
            return isGoalTodosAllSelected(goalId, filteredAllTodos);
        },
        [isGoalTodosAllSelected, filteredAllTodos]
    );

    if (isLoadingGoals || isLoadingNotDoneTodos || isLoadingDoneTodos) {
        return (
            <Center>
                <Loader />
            </Center>
        );
    }

    if (filteredAllTodos?.length === 0) {
        return (
            <Box flex={1}>
                <Text size="sm">
                    {t('feedback:todoList.feedbackPlaceHolder')}
                </Text>
            </Box>
        );
    }

    return (
        <TodoGroupRenderer
            todosByGoalId={groupedTodos.todosByGoalId}
            todosWithoutGoal={groupedTodos.todosWithoutGoal}
            renderTodoItem={renderTodoItem}
            handleSelectAll={handleSelectAll}
            isGoalTodosAllSelected={checkIsGoalTodosAllSelected}
        />
    );
};

// 피드백 요청에 포함된 todo 항목 표시 (요청에 대한 응답으로 피드백 생성하는 경우에 사용)
export const RequestedFeedbackTodoList = ({
    todoDatas,
}: {
    todoDatas: FeedbackRequestTodoWithDetail[];
}) => {
    const { t } = useTranslation(['feedback']);
    const {
        selectedTodoIds,
        handleTodoToggle,
        handleSelectAllByGoalId,
        isGoalTodosAllSelected,
    } = useTodoSelection();

    // goal별로 그룹핑
    const groupedTodos = useMemo(
        () => groupTodosByGoal(todoDatas, t),
        [todoDatas, t]
    );

    const renderTodoItem = useCallback(
        (todo: FeedbackRequestTodoWithDetail) => (
            <SelectableTodoItem
                key={todo.id}
                todo={todo.todo}
                todoId={todo.todo.id}
                isSelected={selectedTodoIds
                    .map(({ id }) => id)
                    .includes(todo.todo.id)}
                onToggle={handleTodoToggle}
            />
        ),
        [selectedTodoIds, handleTodoToggle]
    );

    // 목표별 전체 선택 핸들러
    const handleSelectAll = useCallback(
        (goalId: string) => {
            handleSelectAllByGoalId(goalId, todoDatas);
        },
        [handleSelectAllByGoalId, todoDatas]
    );

    // 목표별로 모든 할일이 선택되었는지 확인하는 함수
    const checkIsGoalTodosAllSelected = useCallback(
        (goalId: string) => {
            return isGoalTodosAllSelected(goalId, todoDatas);
        },
        [isGoalTodosAllSelected, todoDatas]
    );

    if (todoDatas?.length === 0) {
        return (
            <Box flex={1}>
                <Text size="sm">
                    {t('feedback:todoList.requestPlaceHolder')}
                </Text>
            </Box>
        );
    }

    return (
        <TodoGroupRenderer
            todosByGoalId={groupedTodos.todosByGoalId}
            todosWithoutGoal={groupedTodos.todosWithoutGoal}
            renderTodoItem={renderTodoItem}
            handleSelectAll={handleSelectAll}
            isGoalTodosAllSelected={checkIsGoalTodosAllSelected}
        />
    );
};
